import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import { api } from '../services/api';
import { useGroup } from '../contexts/GroupContext.jsx';
import { useEngineStrings } from '../user_guides/i18n/useEngineStrings';
import { useMasking } from './MaskingProvider.jsx';
import { MaskedCurrency } from './MaskedValue';
import FigureNote from './dashboard/FigureNote';
import { subscribeTangibleRefresh } from '../utils/tangibleRefresh';
import { checkingCents } from '../utils/dashboardN';

/**
 * Top-bar metric blocks (v4 board, section 7.1; figures from #2140 and
 * #2206): Checking, Savings, Net Worth and Pending Income (when above zero)
 * from `/tangible`, inline baseline rows 36px apart. The bar keeps the
 * figures that fit on one row, measured (#2215): as room runs out Pending
 * Income goes first (with its hairline), then Savings, then Checking, so Net
 * Worth is always the last left. Below sm a list route shows Net Worth alone
 * on the bar's second row; detail routes (`detailOnly`) show Net Worth alone
 * at any width, so the 44px sticky-hero offset still matches the bar.
 *
 *   Checking       = the active Checking ledgers in `byAccount`, added up
 *   Savings        = savingsLedgerTotalCents
 *   Net Worth      = accountBalanceCents + savingsLedgerTotalCents
 *                    (every ledger added together; positive budgets are NOT
 *                    subtracted, so this is not the API's netWorthCents)
 *   Pending Income = pendingIncomeTotalCents, shown above zero
 *
 * Each label-and-value pair carries its note (DESIGN_SYSTEM.md 8.20). The
 * `header-tangible` tour id stays on the Net Worth pair so guide anchors
 * resolve; its string key keeps the `header.liveNetWorth` name.
 */

const GAP = 36;
// The hairline before Pending Income turns that one 36px gap into 45px
// (22px each side plus its 1px).
const HAIRLINE_EXTRA = 9;

/**
 * The figures that fit in `room` px on one row (#2215), in the drop order:
 * Pending Income, then Savings, then Checking; Net Worth always stays. Width
 * 0 for Pending Income means it is not on the bar.
 * @param {{checking: number, savings: number, liveNetWorth: number, pendingIncome: number}} widths
 * @param {number} room
 * @returns {string[]} figure keys, left to right
 */
export const figuresThatFit = (widths, room) => {
  const all = ['checking', 'savings', 'liveNetWorth'];
  if (widths.pendingIncome > 0) all.push('pendingIncome');
  const need = (keys) => keys.reduce((sum, k) => sum + widths[k], 0)
    + GAP * (keys.length - 1)
    + (keys.includes('pendingIncome') ? HAIRLINE_EXTRA : 0);
  const options = [all, ['checking', 'savings', 'liveNetWorth'], ['checking', 'liveNetWorth']];
  return options.find((keys) => need(keys) <= room) || ['liveNetWorth'];
};

const TangibleDisplay = ({ detailOnly = false }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const strings = useEngineStrings();
  const belowSm = useMediaQuery(theme.breakpoints.down('sm'));
  const { activeGroupId } = useGroup();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isMasked, toggleMasking } = useMasking();
  // A touch tap on a figure pair opens its note and leaves masking alone;
  // a tap on bar space still toggles masking (#2140 settle point 4).
  const tapOnPair = useRef(false);

  // Every figure stays mounted so it can be measured; the ones that do not
  // fit are taken out of the row and hidden (#2215). Measured after every
  // render (amounts and masking change widths) and whenever the room or the
  // row resizes (window width, web fonts arriving).
  const roomRef = useRef(null);
  const rowRef = useRef(null);
  const [fitting, setFitting] = useState(null);
  const measure = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    const roomWidth = room.getBoundingClientRect().width;
    const width = (key) => room.querySelector(`[data-testid="header-${key}"]`)?.getBoundingClientRect().width || 0;
    // Not laid out yet (no room measured): keep every figure.
    const next = roomWidth > 0
      ? figuresThatFit({
        checking: width('checking'),
        savings: width('savings'),
        liveNetWorth: width('liveNetWorth'),
        pendingIncome: width('pendingIncome'),
      }, roomWidth).join(' ')
      : null;
    setFitting((prev) => (prev === next ? prev : next));
  }, []);
  useLayoutEffect(measure);
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(roomRef.current);
    observer.observe(rowRef.current);
    return () => observer.disconnect();
  }, [measure]);

  useEffect(() => {
    if (!activeGroupId) return;
    let cancelled = false;
    const load = async ({ silent = false } = {}) => {
      try {
        if (!silent) setLoading(true);
        setError(null);
        const res = await api.get(activeGroupId, '/tangible');
        if (!cancelled) setData(res || {});
      } catch (err) {
        console.error('Failed to load tangible value:', err);
        if (!cancelled) setError('Failed to calculate tangible value');
      } finally {
        if (!cancelled && !silent) setLoading(false);
      }
    };
    load();
    // Line mutations elsewhere in the app signal this event so the header
    // metrics track reality mid-session (silent: keep current values up
    // while the refetch runs instead of flashing the loading dash).
    // /tangible reads the stream-delta balance cache (#857), so the reloads
    // follow the shared backoff (#1104).
    const unsubscribe = subscribeTangibleRefresh(() => load({ silent: true }));
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [activeGroupId]);

  // v4 board metric blocks: label 10px, bar-label tracking (.08em), uppercase;
  // value 13px medium, sign-colored per section 11 off palette.bar.* (both
  // themes carry explicit bar tokens as of #1377). Blocks sit 36px apart.
  const wrapSx = {
    cursor: 'pointer',
    userSelect: 'none',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'nowrap',
    gap: `${GAP}px`,
    lineHeight: 1.1,
    '&:hover': { opacity: 0.8 },
  };

  const labelSx = {
    fontSize: '10px',
    fontWeight: theme.ds.font.weightMedium,
    color: theme.palette.bar.label,
    textTransform: 'uppercase',
    letterSpacing: theme.ds.font.letterBarLabel,
    lineHeight: 1.2,
  };

  const valueBaseSx = {
    fontSize: `${theme.ds.font.value}px`,
    fontWeight: theme.ds.font.weightMedium,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.3,
  };

  // A figure that does not fit leaves the row (no gap, no width) but keeps
  // its size so the next measure can see it; hidden from sight, the pointer
  // and assistive tech.
  const droppedSx = { position: 'absolute', top: 0, left: 0, visibility: 'hidden', pointerEvents: 'none' };
  const isDropped = (key) => fitting !== null && !fitting.split(' ').includes(key);

  // One figure: the label-and-value pair is the note's target and, for
  // Pending Income, the link itself, so one element carries both.
  const renderChip = (key, valueCents, onClick, tourId) => {
    const label = strings[`header.${key}`];
    const clickable = typeof onClick === 'function';
    const cents = valueCents || 0;
    const { bar } = theme.palette;
    const color = cents < 0 ? bar.negative : cents === 0 ? bar.zero : bar.positive;
    const dropped = isDropped(key);
    return (
      <FigureNote
        key={key}
        note={strings[`header.${key}.note`]}
        aria-hidden={dropped || undefined}
        data-tour-id={tourId}
        data-testid={`header-${key}`}
        onClick={clickable ? onClick : undefined}
        role={clickable ? 'button' : undefined}
        onKeyDown={clickable ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(e);
          }
        } : undefined}
        title={clickable ? strings['header.pendingIncome.link'] : undefined}
        sx={{
          // Kit BrdMetric (#1405): label and value share one baseline row.
          // Inline keeps the bar content short so the fixed 44px bar holds.
          display: 'flex',
          alignItems: 'baseline',
          gap: '9px',
          whiteSpace: 'nowrap',
          ...(clickable ? { cursor: 'pointer' } : {}),
          ...(dropped ? droppedSx : {}),
        }}
      >
        <Typography component="span" sx={labelSx}>
          {label}
        </Typography>
        <Typography component="span" sx={{ ...valueBaseSx, color }}>
          <MaskedCurrency value={cents / 100} />
        </Typography>
      </FigureNote>
    );
  };

  // Clicking "Pending Income" opens the Budgets page, where the Pending
  // Income card is pinned at the top (#906, no scroll since #2179).
  // stopPropagation keeps it from also toggling masking on the wrapping Box.
  const goToPendingIncome = (e) => {
    e.stopPropagation();
    navigate('/budgets');
  };

  const placeholder = (text) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '9px' }}>
      <Typography component="span" sx={labelSx}>{strings['header.liveNetWorth']}</Typography>
      <Typography component="span" sx={{ ...valueBaseSx, color: theme.palette.bar.label }}>{text}</Typography>
    </Box>
  );

  let body;
  if (loading) {
    body = placeholder('-');
  } else if (error) {
    body = placeholder('Error');
  } else {
    const savingsCents = data?.savingsLedgerTotalCents ?? 0;
    const netWorthCents = (data?.accountBalanceCents ?? 0) + savingsCents;
    const pendingIncomeCents = data?.pendingIncomeTotalCents ?? 0;
    const netWorth = renderChip('liveNetWorth', netWorthCents, undefined, 'header-tangible');
    if (detailOnly || belowSm) {
      body = netWorth;
    } else {
      const hasPending = pendingIncomeCents > 0;
      body = (
        <>
          {renderChip('checking', checkingCents(data?.byAccount))}
          {renderChip('savings', savingsCents)}
          {netWorth}
          {hasPending && !isDropped('pendingIncome') && (
            // Hairline before Pending Income: 22px each side, the 36px gap
            // less 14. It never shows without Pending Income after it.
            <Box
              aria-hidden
              data-testid="header-hairline"
              sx={{ width: '1px', height: '16px', alignSelf: 'center', mx: '-14px', backgroundColor: theme.palette.borderStrong }}
            />
          )}
          {hasPending && renderChip('pendingIncome', pendingIncomeCents, goToPendingIncome, 'header-pending-income')}
        </>
      );
    }
  }

  // The room is the bar's left slot; the figure row inside it keeps its own
  // width, so a click on empty bar space past the figures does not mask.
  return (
    <Box ref={roomRef} data-testid="header-metrics-room" sx={{ position: 'relative', display: 'flex', flex: 1, minWidth: 0, overflow: 'hidden' }}>
      <Box
        ref={rowRef}
        data-testid="header-metrics"
        onPointerDownCapture={(e) => {
          tapOnPair.current = e.pointerType === 'touch' && Boolean(e.target.closest('[data-figure-note]'));
        }}
        onClick={() => {
          if (tapOnPair.current) {
            tapOnPair.current = false;
            return;
          }
          toggleMasking();
        }}
        sx={wrapSx}
        title={isMasked ? 'Click to show real values' : 'Click to mask sensitive data'}
      >
        {body}
      </Box>
    </Box>
  );
};

export default TangibleDisplay;
